import { type Home, UserRole } from '@lmc/ts-common'
import {
  Button,
  EmptyState,
  Form,
  FormCard,
  FormItem,
  PageHeader,
  TextArea
} from '@lmc/ui-kit'
import type { CircumstanceSubsectionResponse } from '@lmc/validations'
import { Form as AntdForm } from 'antd'
import { getNominative, showToast } from 'common/utils'
import { useRoleAccess } from 'components/AccessControl'
import LastUpdateBox from 'components/carePlans/LastUpdateBox'
import { lastUpdateCarerFromUpdatedByData } from 'components/carePlans/lastUpdateCarerFromUpdatedByData'
import { Spinner } from 'components/generic'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useResident, {
  convertFormTypeToResidentBody,
  type ResidentItemExtended,
  type ResidentUpdateBody
} from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ButtonsWrapper,
  ProfileContent,
  ProfileWrapper
} from '../../styledComponents'
import { handleOnFinishFailed, scrollToTop } from '../../utils/utils'

type CircumstancesFormValues = {
  circumstances: {
    socialCircumstances: { description: string }
    educationalHistory: { description: string }
    occupationalHistory: { description: string }
  }
}

const ResidentCircumstances = ({ testId }: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [form] = AntdForm.useForm<CircumstancesFormValues>()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement>(null)
  const { id } = useParams()
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const { resident, isLoading, updateResident } = useResident({
    id,
    onUpdateSuccess: () => {
      showToast({
        title: t(supConstants.RESIDENT_UPDATED, {
          variable: residentNominative
        })
      })
      navigate(`../profile`, { relative: 'path' })
    }
  })
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)

  const handleOnFinish = async (values: CircumstancesFormValues) => {
    setIsUpdateLoading(true)
    // Before await: useResident’s onUpdateSuccess navigates as soon as the mutation
    // succeeds; clearing dirty first matches ResidentContactDetails and avoids blocking that navigation.
    setIsDirty(false)
    try {
      const payload: ResidentUpdateBody = convertFormTypeToResidentBody(
        values,
        (v) => ({
          circumstances: {
            socialCircumstances: {
              description: v.circumstances.socialCircumstances.description
            },
            educationalHistory: {
              description: v.circumstances.educationalHistory.description
            },
            occupationalHistory: {
              description: v.circumstances.occupationalHistory.description
            }
          }
        })
      )
      const response = await updateResident(payload)
      if (!(response && typeof response === 'object' && 'result' in response)) {
        setIsDirty(true)
        return
      }
    } catch {
      setIsDirty(true)
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    } finally {
      setIsUpdateLoading(false)
    }
  }

  const handleOnCancel = () => {
    navigate(`../profile`, { relative: 'path' })
  }

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'Resident'
      }
    ]
  )

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId ? `${testId}-circumstances-loading` : 'circumstances-loading'
        }
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId
            ? `${testId}-circumstances-no-resident`
            : 'circumstances-no-resident'
        }
      />
    )
  }

  const r = resident as ResidentItemExtended

  const initialValues: CircumstancesFormValues = {
    circumstances: {
      socialCircumstances: {
        description: r.circumstances?.socialCircumstances?.description ?? ''
      },
      educationalHistory: {
        description: r.circumstances?.educationalHistory?.description ?? ''
      },
      occupationalHistory: {
        description: r.circumstances?.occupationalHistory?.description ?? ''
      }
    }
  }

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={
        testId ? `${testId}-circumstances-wrapper` : 'circumstances-wrapper'
      }
    >
      <PageHeader
        sticky
        title={t(supConstants.CIRCUMSTANCES)}
        data-testid={
          testId ? `${testId}-circumstances-header` : 'circumstances-header'
        }
      >
        <ButtonsWrapper
          data-testid={
            testId ? `${testId}-circumstances-buttons` : 'circumstances-buttons'
          }
        >
          <Button
            data-cy="cancel-btn"
            onClick={handleOnCancel}
            data-testid={
              testId
                ? `${testId}-circumstances-cancel-btn`
                : 'circumstances-cancel-btn'
            }
          >
            {t(supConstants.CANCEL)}
          </Button>
          <Button
            data-cy="submit-btn"
            variant="primary"
            onClick={() => form.submit()}
            type="submit"
            isLoading={isUpdateLoading}
            disabledByPermission={!hasRolePermission}
            data-testid={
              testId
                ? `${testId}-circumstances-save-btn`
                : 'circumstances-save-btn'
            }
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent
        data-testid={
          testId ? `${testId}-circumstances-content` : 'circumstances-content'
        }
      >
        <NavigationModal canNavigate={!isDirty} />
        <Form<CircumstancesFormValues>
          form={form}
          onFinish={handleOnFinish}
          initialValues={initialValues}
          onValuesChange={() => setIsDirty(true)}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          data-testid={
            testId ? `${testId}-circumstances-form` : 'circumstances-form'
          }
        >
          <CircumstanceSubsectionCard
            title={t(supConstants.SOCIAL_CIRCUMSTANCES)}
            namePath={['circumstances', 'socialCircumstances', 'description']}
            subsection={r.circumstances?.socialCircumstances}
            testId={testId}
            cardTestId="social-circumstances"
          />
          <CircumstanceSubsectionCard
            title={t(supConstants.EDUCATIONAL_HISTORY)}
            namePath={['circumstances', 'educationalHistory', 'description']}
            subsection={r.circumstances?.educationalHistory}
            testId={testId}
            cardTestId="educational-history"
          />
          <CircumstanceSubsectionCard
            title={t(supConstants.OCCUPATIONAL_HISTORY)}
            namePath={['circumstances', 'occupationalHistory', 'description']}
            subsection={r.circumstances?.occupationalHistory}
            testId={testId}
            cardTestId="occupational-history"
          />
        </Form>
      </ProfileContent>
    </ProfileWrapper>
  )
}

function CircumstanceSubsectionCard({
  title,
  namePath,
  subsection,
  testId,
  cardTestId
}: {
  title: string
  namePath: (string | number)[]
  subsection?: CircumstanceSubsectionResponse
  testId?: string
  cardTestId: string
}) {
  return (
    <FormCard
      title={title}
      testId={testId ? `${testId}-${cardTestId}-card` : `${cardTestId}-card`}
    >
      <FormItem name={namePath}>
        <TextArea autoSize={{ minRows: 3 }} />
      </FormItem>
      {subsection?.updatedAt ? (
        <LastUpdateBox
          date={subsection.updatedAt}
          carer={lastUpdateCarerFromUpdatedByData(subsection.updatedByData)}
          noDateText=""
          style={{ paddingInline: 0 }}
        />
      ) : null}
    </FormCard>
  )
}

export default ResidentCircumstances
